import React, { useEffect, useState } from 'react';
import { Button } from '@components';
import { Card, Form, Row, Col, ButtonGroup } from 'react-bootstrap';
import { Formik } from 'formik';
import * as yup from 'yup';
import { useLocation, useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Get, Put } from '@app/utils/httpProvider';
import { CHUYENNGANH_API, CHUYENKHOA_API } from '@app/utils/apiUrl';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { messageError, messageToast, status200, styleSelectDefault } from '../../../../utils/define';
import { URI_INTERNAL } from '../../../../utils/pathLocations';
import HorizontalFieldsRow from '@app/components/common/HorizontalFieldsRow';
import { AsyncPaginate } from 'react-select-async-paginate';

const schema = yup.object().shape({
    chuyenKhoaId: yup.string().required('Chuyên khoa không được để trống'),
    maChuyenNganh: yup.string().required(messageError.emptyMessage).max(50, 'Mã chuyên ngành tối đa 50 ký tự'),
    tenChuyenNganh: yup.string().required(messageError.emptyMessage).max(255, 'Tên chuyên ngành tối đa 255 ký tự')
});

const ChuyenNganhEdit = () => {
    const history = useHistory();
    const queryClient = useQueryClient();
    const location = useLocation();
    const params = new URLSearchParams(location.search.substring(1));
    const itemId = params.get('id');

    const itemQuery = useQuery(['chuyenNganh', itemId], () => Get(CHUYENNGANH_API.GET_ITEM, itemId), {
        enabled: !!itemId
    });

    const editMutation = useMutation((value) => Put(CHUYENNGANH_API.UPDATE_ITEM, itemId, value));

    const handleEdit = (values) => {
        editMutation.mutate(values, {
            onSuccess: (res) => {
                if (res.status !== status200) {
                    toast.error(messageToast.messageToastUpdateError);
                } else {
                    toast.success(messageToast.messageToastUpdateSuccess);
                    queryClient.setQueryData(['chuyenNganh', itemId], values);
                    history.push(URI_INTERNAL.CHUYENNGANH_PATH);
                }
            },
            onError: (err) => {
                toast.error(err?.message || 'Đã có lỗi xảy ra, vui lòng thử lại !');
            }
        });
    };

    const loadOptionsChuyenKhoa = async (search, loadedOptions, { page }) => {
            const pageSize = 10;
            const res = await Get(CHUYENKHOA_API.GET_PAGING, null, {
                PageIndex: page,
                PageSize: pageSize,
                Keyword: search // pass the input text to filter server-side
            });
    
            const data = res.data || [];
            const options = data.map((x) => ({
                label: `${x.maChuyenKhoa} - ${x.tenChuyenKhoa}`,
                value: x.id
            }));
    
            // assume your API returns total count or a flag for more pages
            const hasMore = data.length === pageSize;
            return {
                options,
                hasMore,
                additional: { page: page + 1 }
            };
        };

    if (itemQuery.isLoading) return <div>Đang tải....</div>;
    if (itemQuery.isError || !itemQuery.data) return <div>Có lỗi xảy ra hoặc không tìm thấy chuyên ngành</div>;

    const currentData = itemQuery.data;

    return (
        <Card>
            <Card.Header as="h5">Sửa chuyên ngành</Card.Header>
            <Card.Body>
                <Formik
                    enableReinitialize
                    validationSchema={schema}
                    onSubmit={(values) => handleEdit(values)}
                    initialValues={{
                        chuyenKhoaId: currentData
                            .chuyenKhoaId != NIL_UUID
                            ? {
                                label: currentData.chuyenKhoa?.maChuyenKhoa + " - " + currentData.chuyenKhoa?.tenChuyenKhoa,
                                value: currentData.chuyenKhoaId
                            }
                            : {},
                        maChuyenNganh: currentData.maChuyenNganh || currentData.MaChuyenNganh || '',
                        tenChuyenNganh: currentData.tenChuyenNganh || currentData.TenChuyenNganh || '',
                        moTa: currentData.moTa || currentData.MoTa || ''
                    }}
                >
                    {({ handleSubmit, handleChange, values, errors, setFieldValue }) => (
                        <Form noValidate onSubmit={handleSubmit}>
                            <Form.Group as={Row} className="mb-3 align-items-center">
                                <Form.Label column sm={2}>Chuyên khoa</Form.Label>
                                <Col sm={10}>
                                    <AsyncPaginate
                                        name="chuyenKhoaId"
                                        id="chuyenKhoaId"
                                        placeholder="Chọn chuyên khoa"
                                        value={values.chuyenKhoaId}
                                        loadOptions={loadOptionsChuyenKhoa}
                                        styles={styleSelectDefault}
                                        isClearable
                                        onChange={(selectedOption) => {
                                            // const event = {
                                            //     target: {
                                            //         name: 'accountPatients',
                                            //         value: selectedOption
                                            //     }
                                            // };
                                            // setAccountHisId(event);
                                            setFieldValue(
                                                'chuyenKhoaId',
                                                selectedOption,
                                                true
                                            );
                                        }}
                                        additional={{
                                            page: 1
                                        }}
                                    />
                                    <div
                                        style={{
                                            display: 'block',
                                            color: '#dc3545'
                                        }}
                                        className="invalid-feedback"
                                    >
                                        {errors.chuyenKhoaId}
                                    </div>
                                </Col>
                            </Form.Group>

                            <HorizontalFieldsRow
                                fields={[
                                    {
                                        label: 'Mã chuyên ngành',
                                        name: 'maChuyenNganh',
                                        value: values.maChuyenNganh,
                                        onChange: handleChange,
                                        isInvalid: errors.maChuyenNganh,
                                        error: errors.maChuyenNganh
                                    },
                                    {
                                        label: 'Tên chuyên ngành',
                                        name: 'tenChuyenNganh',
                                        value: values.tenChuyenNganh,
                                        onChange: handleChange,
                                        isInvalid: errors.tenChuyenNganh,
                                        error: errors.tenChuyenNganh
                                    },
                                    {
                                        label: 'Mô tả',
                                        name: 'moTa',
                                        value: values.moTa,
                                        onChange: handleChange,
                                        type: 'text'
                                    }
                                ]}
                            />

                            <Form.Group as={Row} className="mb-3">
                                <Col md={{ span: 10, offset: 2 }}>
                                    <ButtonGroup className="mb-2">
                                        <Button type="submit" theme="primary">Cập nhật</Button>
                                        &nbsp;&nbsp;&nbsp;
                                        <Button onClick={() => history.push(URI_INTERNAL.CHUYENNGANH_PATH)} theme="secondary">Hủy</Button>
                                    </ButtonGroup>
                                </Col>
                            </Form.Group>
                        </Form>
                    )}
                </Formik>
            </Card.Body>
        </Card>
    );
};

export default ChuyenNganhEdit;
